import { useEffect, useState, type FormEvent } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { PageShell } from '../components/BrandChrome'
import { Alert, Button, Card, Input, Label, Subtitle, Title } from '../components/ui'
import { errorMessage } from '../lib/format'

export function MfaSetupPage() {
  const navigate = useNavigate()
  const { refresh, user } = useAuth()
  const [otpauth, setOtpauth] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [recovery, setRecovery] = useState<string[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        await api.me()
        const setup = await api.mfaSetup()
        if (!cancelled) setOtpauth(setup.otpauth_uri)
      } catch (err) {
        if (!cancelled) setError(errorMessage(err))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  async function onConfirm(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api.me()
      const res = await api.mfaConfirm(code.trim())
      setRecovery(res.recovery_codes)
      await refresh()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const secret = otpauth?.match(/secret=([^&]+)/i)?.[1] ?? null

  return (
    <PageShell homeTo="/drops">
      <Card className="mx-auto max-w-lg">
        <Title>Set up authenticator</Title>
        <Subtitle>
          Two-factor authentication is required before you can create drops
          {user?.email ? ` for ${user.email}` : ''}.
        </Subtitle>

        {recovery ? (
          <div className="space-y-4">
            <Alert tone="success">MFA enrolled. Store these recovery codes somewhere safe — shown once.</Alert>
            <ul className="grid grid-cols-1 gap-2 rounded-lg bg-slate-100 p-3 font-mono text-sm sm:grid-cols-2">
              {recovery.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <Button type="button" className="w-full" onClick={() => navigate('/drops', { replace: true })}>
              Continue to drops
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {error ? <Alert>{error}</Alert> : null}
            {otpauth ? (
              <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <QRCodeSVG value={otpauth} size={160} />
                </div>
                <div className="text-sm text-slate-600">
                  <p className="mb-2">Scan with your authenticator app, or enter the secret manually:</p>
                  {secret ? (
                    <code className="block break-all rounded bg-slate-100 px-2 py-1 font-mono text-xs">
                      {secret}
                    </code>
                  ) : null}
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500">Loading setup…</p>
            )}
            <form className="space-y-3" onSubmit={onConfirm}>
              <div>
                <Label htmlFor="totp">6-digit code</Label>
                <Input
                  id="totp"
                  name="totp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  required
                  value={code}
                  onChange={(ev) => setCode(ev.target.value)}
                  className="text-center text-lg tracking-widest"
                />
              </div>
              <Button type="submit" disabled={busy || !otpauth} className="w-full">
                {busy ? 'Confirming…' : 'Confirm MFA'}
              </Button>
            </form>
            <p className="text-center text-sm text-slate-600">
              <Link to="/mfa/recover">Lost access? Recover MFA</Link>
            </p>
          </div>
        )}
      </Card>
    </PageShell>
  )
}
